import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShareNetwork, Copy, WhatsappLogo, FacebookLogo, TwitterLogo, TelegramLogo, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

const ShareGate = ({ concertId, concertTitle, artistName, onUnlocked }) => {
  const [shared, setShared] = useState(false);
  const [shareCount, setShareCount] = useState(0);

  const url = `${window.location.origin}/concert/${concertId}`;
  const text = `🎤 Je vais voir ${artistName} en live sur XooKmédia ! "${concertTitle}". Rejoignez-moi : ${url}`;

  const recordShare = async () => {
    try {
      const { data } = await api.post(`/concerts/${concertId}/share`);
      setShareCount(data.share_count);
      setShared(true);
      toast.success("Partage validé ! Accès débloqué 🎉");
      setTimeout(() => onUnlocked?.(), 800);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié !");
    recordShare();
  };

  const shareVia = (platform) => {
    const enc = encodeURIComponent(text);
    const links = {
      whatsapp: `https://wa.me/?text=${enc}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${enc}`,
      twitter: `https://twitter.com/intent/tweet?text=${enc}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${enc}`,
      email: `mailto:?subject=${encodeURIComponent("XooKmédia live")}&body=${enc}`,
    };
    window.open(links[platform], "_blank", "noopener,noreferrer");
    recordShare();
  };

  return (
    <div className="rounded-2xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#1a1206] to-[#0f0f10] p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
          <ShareNetwork size={22} className="text-[#F59E0B]" weight="duotone" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#F59E0B] font-bold">Accès viral</div>
          <h3 className="font-display font-bold text-lg">Partagez pour débloquer le live</h3>
        </div>
      </div>
      <p className="text-zinc-400 text-sm mb-5 leading-relaxed">
        Partagez le concert avec votre cercle (1 partage minimum). C'est ce qui rend XooKmédia viral et soutient l'artiste !
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <button data-testid="share-whatsapp" onClick={() => shareVia("whatsapp")} className="p-3 rounded-lg border border-white/10 bg-black/40 hover:border-[#10B981] hover:text-[#10B981] transition-colors flex flex-col items-center gap-1 text-xs">
          <WhatsappLogo size={22} weight="fill" /> WhatsApp
        </button>
        <button data-testid="share-facebook" onClick={() => shareVia("facebook")} className="p-3 rounded-lg border border-white/10 bg-black/40 hover:border-[#1877F2] hover:text-[#1877F2] transition-colors flex flex-col items-center gap-1 text-xs">
          <FacebookLogo size={22} weight="fill" /> Facebook
        </button>
        <button data-testid="share-twitter" onClick={() => shareVia("twitter")} className="p-3 rounded-lg border border-white/10 bg-black/40 hover:border-white hover:text-white transition-colors flex flex-col items-center gap-1 text-xs">
          <TwitterLogo size={22} weight="fill" /> X / Twitter
        </button>
        <button data-testid="share-telegram" onClick={() => shareVia("telegram")} className="p-3 rounded-lg border border-white/10 bg-black/40 hover:border-[#0088cc] hover:text-[#0088cc] transition-colors flex flex-col items-center gap-1 text-xs">
          <TelegramLogo size={22} weight="fill" /> Telegram
        </button>
        <button data-testid="share-email" onClick={() => shareVia("email")} className="p-3 rounded-lg border border-white/10 bg-black/40 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors flex flex-col items-center gap-1 text-xs">
          <EnvelopeSimple size={22} weight="fill" /> Email
        </button>
      </div>

      <Button data-testid="share-copy" onClick={copyLink} variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 hover:text-white">
        <Copy className="mr-2" weight="bold" /> Copier le lien
      </Button>

      {shared && (
        <div className="mt-4 p-3 rounded-lg bg-[#10B981]/15 border border-[#10B981]/30 flex items-center gap-2 text-sm" data-testid="share-success">
          <CheckCircle weight="fill" className="text-[#10B981]" /> Partage validé ({shareCount}) — accès débloqué !
        </div>
      )}
    </div>
  );
};

export default ShareGate;
